import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import axios from "axios";
import { MantineProvider } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@mantine/core/styles.css";
import "@mantine/tiptap/styles.css";

import App from "./App";

import "@style/main.css";

import axiosDefaults from "@util/axiosDefaults";
import { initSentry, setSentryUserFromStore, Sentry } from "@util/sentry";
import i18n from "@locales/index.js";
import { appLanguages } from "./lib/appLanguages";

initSentry();

axios.defaults.baseURL = "/api";
axios.interceptors.request.use(
  (request) => {
    request.headers["x-language"] = i18n.language;
    return request;
  },
  (error) => Promise.reject(error)
);

function ReactApp() {
  const queryClient = new QueryClient();

  useEffect(() => {
    setSentryUserFromStore();
  }, []);

  useEffect(() => {
    const localStorageLanguage = localStorage.getItem("cs_app_language");

    if (localStorageLanguage) {
      // check if the language is supported
      if (
        appLanguages.some(
          (language) => language.language === localStorageLanguage
        )
      ) {
        i18n.changeLanguage(localStorageLanguage);
      } else {
        // set default language to en
        setLocalStorageLanguage("en");
      }
    } else {
      // lets try to get the language from the browser first
      const browserLanguageCode = navigator?.language?.split("-")[0];
      // lets see if the language is supported
      if (
        appLanguages.some(
          (language) => language.language === browserLanguageCode
        )
      ) {
        i18n.changeLanguage(browserLanguageCode);
        setLocalStorageLanguage(browserLanguageCode);
      } else {
        // set default language to en
        setLocalStorageLanguage("en");
      }
    }
  }, []);

  function setLocalStorageLanguage(language) {
    localStorage.setItem("cs_app_language", language);
  }
  return (
    <Sentry.ErrorBoundary
      fallback={<div>Something went wrong. Please refresh and try again.</div>}
      showDialog={false}
    >
      <QueryClientProvider client={queryClient}>
        <MantineProvider
          theme={{
            components: {
              Card: {
                defaultProps: {
                  radius: "md",
                  p: "sm",
                  withBorder: true,
                  style: {
                    overflow: "visible",
                  },
                },
              },
              Switch: {
                defaultProps: {
                  style: {
                    label: {
                      marginRight: "5px",
                    },
                  },
                },
              },
            },
            fontFamily: "DM Sans",
            fontFamilyMonospace: "DM Sans, monospace",
            headings: { fontFamily: "DM Sans, sans-serif" },
          }}
        >
          <App style={{ height: "100%" }} />
        </MantineProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

const container = document.getElementById("app");
const root = createRoot(container);

root.render(<ReactApp />);
